import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { UiButtonComponent } from '@ui';
import { IntelligenceModels } from '@models/intelligence.model';
import { IntelligenceResultsService } from '../../../../../../services/intelligence-results.service';
import { EnableEscCloseDirective } from '../../../../../directives/enable-esc-close.directive';
import { ChartResultCanvasComponent } from '../chart-result-canvas/chart-result-canvas.component';
import {
  buildChartDrilldownPrompt,
  isChartBarActionable,
} from '../chart-result-drilldown.util';
import {
  formatIdentifier,
  formatTimeRange,
  timeframeTranslationKey,
} from '../chart-result.util';

const DUPLICATE_CLICK_DEBOUNCE_MS = 500;

/**
 * The chart at full size. It renders the same canvas component as the inline
 * view, so there is one chart implementation.
 */
@Component({
  selector: 'app-chart-result-expanded-dialog',
  standalone: true,
  templateUrl: './chart-result-expanded-dialog.component.html',
  styleUrl: './chart-result-expanded-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatDialogModule,
    MatTooltip,
    TranslatePipe,
    UiButtonComponent,
    ChartResultCanvasComponent,
  ],
  hostDirectives: [EnableEscCloseDirective],
})
export class ChartResultExpandedDialogComponent {
  public readonly response: IntelligenceModels.Charts.Response =
    inject<IntelligenceModels.Charts.Response>(MAT_DIALOG_DATA);

  public readonly title: string;
  public readonly timeframeLabel: string;
  public readonly timeRangeLabel: string;
  public readonly isBarActionable: IntelligenceModels.Charts.BarSelectionPredicate =
    (selection) => isChartBarActionable(this.response, selection);

  private readonly dialogRef =
    inject<MatDialogRef<ChartResultExpandedDialogComponent>>(MatDialogRef);
  private readonly translateService = inject(TranslateService);
  private readonly resultsService = inject(IntelligenceResultsService);
  private lastSelectionKey: string | null = null;
  private lastSelectionAt = 0;

  public constructor() {
    const { plotType, timeframe, timeRange } = this.response;
    const timeframeKey = timeframeTranslationKey(timeframe);

    this.title = formatIdentifier(plotType ?? '');
    this.timeframeLabel = timeframeKey
      ? this.translateService.instant(timeframeKey)
      : timeframe
        ? formatIdentifier(timeframe)
        : '';
    this.timeRangeLabel = formatTimeRange(timeRange, timeframe);
  }

  public close(): void {
    this.dialogRef.close();
  }

  public onBarSelected(selection: IntelligenceModels.Charts.BarSelection): void {
    const prompt = buildChartDrilldownPrompt(this.response, selection);
    if (!prompt || this.isDuplicateSelection(selection)) return;
    this.dialogRef.close();
    this.resultsService.requestPrompt(prompt);
  }

  private isDuplicateSelection(
    selection: IntelligenceModels.Charts.BarSelection,
  ): boolean {
    const key = `${selection.labelIndex}:${selection.seriesIndex}`;
    const now = Date.now();
    const duplicate =
      key === this.lastSelectionKey &&
      now - this.lastSelectionAt < DUPLICATE_CLICK_DEBOUNCE_MS;
    this.lastSelectionKey = key;
    this.lastSelectionAt = now;
    return duplicate;
  }
}
